const MAX_LETTER_COUNT = 23;

/**
 * Truncates a string of text
 *
 * @param {string} text - A string of text.
 * @param {number} maxCharacters -  Max set of characters before the text is truncated.
 * @return {string} Truncated text that is followed by ellipse.
 */

function truncateText(
  text: string,
  maxCharacters: number = MAX_LETTER_COUNT,
): string {
  return (
    text
      .split('')
      .slice(0, maxCharacters)
      .join('') + '...'
  );
}

/**
 * A number, or a string containing a number.
 *
 * @typedef {Object} Labels
 * @property {string} renderedLabel - Label the will render on screen.
 * @property {string} screenReaderLabel - Accessibility label that will be announce when using a screen reader.
 */

/**
 * Outputs display and screen reader labels for Filter Item Button.
 *
 * @param {string} labels.displayLabel - The visible label which by default will show the `label` prop value.
 * @param {string} labels.label -  Accessible name for the  `Filter.Item`
 * @return {Labels} Return display and accessibility label.
 */

export function renderLabels({
  displayLabel,
  label,
}: {
  displayLabel: null | string | number | string[];
  label: string;
}) {
  if (
    typeof displayLabel === 'string' &&
    displayLabel.split('').length > MAX_LETTER_COUNT
  ) {
    return {
      renderedLabel: truncateText(displayLabel),
      screenReaderLabel: `Applied filter for ${label} is ${displayLabel}.`,
    };
  }

  if (typeof displayLabel === 'string') {
    return {
      renderedLabel: displayLabel,
      screenReaderLabel: displayLabel + ` filter`,
    };
  }

  if (typeof displayLabel === 'number') {
    return {
      renderedLabel: displayLabel.toString(),
      screenReaderLabel: `Applied filter for ${label} is ${displayLabel}.`,
    };
  }

  if (Array.isArray(displayLabel)) {
    if (displayLabel.length === 0) {
      return { renderedLabel: label, screenReaderLabel: label + ` Filter` };
    }
    if (displayLabel.length === 1) {
      if (displayLabel[0].split('').length > MAX_LETTER_COUNT) {
        return {
          renderedLabel: `${truncateText(displayLabel[0])}`,
          screenReaderLabel: `Applied filter for ${label} is ${displayLabel[0]}.`,
        };
      }
      return {
        renderedLabel: displayLabel[0],
        screenReaderLabel: `Applied filter for ${label} is ${displayLabel}.`,
      };
    }

    if (displayLabel.length > 1) {
      const plusFilterCount = displayLabel.slice(1).length;
      const screenReaderLabelTemplate = `Applied filters for ${label} are ${displayLabel[0]} plus ${plusFilterCount} more.`;

      if (displayLabel[0].split('').length > MAX_LETTER_COUNT) {
        return {
          renderedLabel: `${truncateText(displayLabel[0])} +${plusFilterCount}`,
          screenReaderLabel: screenReaderLabelTemplate,
        };
      }

      return {
        renderedLabel: `${displayLabel[0]} +${plusFilterCount}`,
        screenReaderLabel: screenReaderLabelTemplate,
      };
    }
  }
  return { renderedLabel: label, screenReaderLabel: label + ` filter` };
}
